import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CalendarService, CalendarMovie, CalendarResponse } from './calendar.service';
import { SeoService } from '../../core/services/seo/seo.service';
import { NormalNavbar } from '../../layout/normal-navbar/normal-navbar';
import { Footer } from '../../layout/footer/footer.component';
import { MediaCardComponent } from '../../shared/components/media-card/media-card.component';

interface CalendarDay {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  movies: CalendarMovie[];
}

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [CommonModule, RouterLink, NormalNavbar, Footer, MediaCardComponent],
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent implements OnInit {
  currentYear = new Date().getFullYear();
  currentMonth = new Date().getMonth(); // 0-indexed

  displayYear = this.currentYear;
  displayMonth = this.currentMonth;

  calendarDays: CalendarDay[] = [];
  upcomingMovies: CalendarMovie[] = [];
  selectedMovie: CalendarMovie | null = null;

  loading = false;
  error = '';

  private monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  private dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  get monthName(): string {
    return this.monthNames[this.displayMonth];
  }

  get dayLabelsList(): string[] {
    return this.dayLabels;
  }

  get totalReleasesCount(): number {
    return this.calendarDays.reduce((acc, day) => acc + (day.isCurrentMonth ? day.movies.length : 0), 0);
  }

  formattedName(name?: string): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s-]+/g, '-');
  }

  selectedDayMovies: CalendarMovie[] | null = null;
  selectedDayDate: Date | null = null;

  constructor(
    private calendarService: CalendarService,
    private seoService: SeoService
  ) {}

  ngOnInit(): void {
    this.seoService.setSeo({
      title: 'Movie Release Calendar & Schedule',
      description: 'Track upcoming Hollywood movie theatrical releases, premiere dates, and calendar schedules on The Scenetale.',
      url: '/scenetale-calendar',
      type: 'website'
    });
    this.loadCalendar();
  }

  prevMonth(): void {
    this.displayMonth--;
    if (this.displayMonth < 0) {
      this.displayMonth = 11;
      this.displayYear--;
    }
    this.loadCalendar();
  }

  nextMonth(): void {
    this.displayMonth++;
    if (this.displayMonth > 11) {
      this.displayMonth = 0;
      this.displayYear++;
    }
    this.loadCalendar();
  }

  goToToday(): void {
    this.displayYear = this.currentYear;
    this.displayMonth = this.currentMonth;
    this.loadCalendar();
  }

  selectMovie(movie: CalendarMovie, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedMovie = movie;
  }

  selectDay(day: CalendarDay, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (day.movies && day.movies.length > 0) {
      if (day.movies.length === 1) {
        this.selectedMovie = day.movies[0];
      } else {
        this.selectedDayMovies = day.movies;
        this.selectedDayDate = day.date;
      }
    }
  }

  closeDetail(): void {
    this.selectedMovie = null;
  }

  closeDayModal(): void {
    this.selectedDayMovies = null;
    this.selectedDayDate = null;
  }

  posterUrl(posterPath: string | null): string {
    if (!posterPath) {
      return 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="150" viewBox="0 0 24 24" fill="%2312b886"%3E%3Crect width="100%25" height="100%25" fill="%23111827"/%3E%3Cpath fill="%2312b886" d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/%3E%3C/svg%3E';
    }
    return `https://image.tmdb.org/t/p/w342${posterPath}`;
  }

  backdropUrl(path: string | null): string {
    if (!path) return '';
    return `https://image.tmdb.org/t/p/w780${path}`;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="150" viewBox="0 0 24 24" fill="%2312b886"%3E%3Crect width="100%25" height="100%25" fill="%23111827"/%3E%3Cpath fill="%2312b886" d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/%3E%3C/svg%3E';
  }

  ratingStars(rating: number): string {
    const stars = Math.round(rating / 2);
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
  }

  loadCalendar(): void {
    this.loading = true;
    this.error = '';
    this.selectedMovie = null;
    this.selectedDayMovies = null;

    // TMDB month is 1-indexed
    const apiMonth = this.displayMonth + 1;

    this.calendarService.getCalendarMovies(this.displayYear, apiMonth).subscribe({
      next: (data: CalendarResponse) => {
        this.buildCalendarGrid(data);
        this.upcomingMovies = data.upcoming || [];
        this.loading = false;
      },
      error: (err: any) => {
        this.error = 'Failed to load calendar. Please try again.';
        this.loading = false;
        console.error('Calendar API error:', err);
      },
    });
  }

  private buildCalendarGrid(data: CalendarResponse): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDay = new Date(this.displayYear, this.displayMonth, 1);
    const lastDay = new Date(this.displayYear, this.displayMonth + 1, 0);

    const startPad = firstDay.getDay(); // 0=Sun
    const totalDays = lastDay.getDate();

    const days: CalendarDay[] = [];

    // Pad with previous month days
    const prevMonthLast = new Date(this.displayYear, this.displayMonth, 0);
    for (let i = startPad - 1; i >= 0; i--) {
      const d = prevMonthLast.getDate() - i;
      const date = new Date(this.displayYear, this.displayMonth - 1, d);
      days.push({
        date,
        dayOfMonth: d,
        isCurrentMonth: false,
        isToday: false,
        movies: [],
      });
    }

    // Current month days
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(this.displayYear, this.displayMonth, d);
      const dateKey = this.formatDateKey(date);
      days.push({
        date,
        dayOfMonth: d,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        movies: data.days?.[dateKey] || [],
      });
    }

    // Pad with next month days to fill grid (always show 6 rows = 42 cells)
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      const date = new Date(this.displayYear, this.displayMonth + 1, d);
      days.push({
        date,
        dayOfMonth: d,
        isCurrentMonth: false,
        isToday: false,
        movies: [],
      });
    }

    this.calendarDays = days;
  }

  private formatDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  trackByDay(_index: number, day: CalendarDay): number {
    return day.date.getTime();
  }

  trackByMovie(_index: number, movie: CalendarMovie): number {
    return movie.id;
  }
}
